import type { AccountSettings, Note } from "./schemas";
import { type Clock, formatClock, formatMonthDay, formatWeekday, minutesOfDay } from "./time";
import { dayNumber } from "./zone";

export type BoardNote = Note & { waiting?: boolean };

export type NoteStamp = { kind: "justNow" } | { kind: "at"; text: string };

const TILTS = [-2, 1.5, -1, 2.5] as const;

type TiltIndex = 0 | 1 | 2 | 3;

export const BOARD_TEXT_MAX = 120;

const JUST_NOW_MS = 5 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

export function noteTilt(noteCount: number): number {
  return TILTS[(noteCount % TILTS.length) as TiltIndex];
}

export function passesBoardLimit(text: string): boolean {
  return text.length > BOARD_TEXT_MAX;
}

export function boardText(text: string): string {
  if (!passesBoardLimit(text)) return text;

  const head = text.slice(0, BOARD_TEXT_MAX);

  return `${atWord(head, text[BOARD_TEXT_MAX] ?? "")}…`;
}

export function noteStamp(createdAt: string, now: Date, clock: Clock, tz: string): NoteStamp {
  const created = new Date(createdAt);

  if (now.getTime() - created.getTime() < JUST_NOW_MS) return { kind: "justNow" };

  return { kind: "at", text: stampText(created, now, clock, tz) };
}

export function noteExpired(
  note: Pick<Note, "createdAt">,
  expiry: AccountSettings["noteExpiry"],
  now: Date,
): boolean {
  if (!expiry.on) return false;

  return now.getTime() - Date.parse(note.createdAt) > expiry.days * DAY_MS;
}

export function noteWaitingToPost(note: { pending: boolean; version: number }): boolean {
  return note.pending && note.version === 0;
}

function stampText(created: Date, now: Date, clock: Clock, tz: string): string {
  const daysAgo = dayNumber(now, tz) - dayNumber(created, tz);

  if (daysAgo === 0) return formatClock(minutesOfDay(created, tz), clock);

  if (daysAgo >= 7) return formatMonthDay(created, tz);

  return `${formatWeekday(created, tz)} ${formatClock(minutesOfDay(created, tz), clock)}`;
}

function atWord(head: string, next: string): string {
  if (/\s/.test(next)) return head.trimEnd();

  const cut = head.replace(/\s+\S*$/, "");

  return cut === head ? head : cut.trimEnd();
}
